import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/auth/token/refresh/`,
          { refresh: refreshToken }
        );

        const { access } = response.data;
        localStorage.setItem('access_token', access);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  // Authentication
  login: (credentials) => api.post('/auth/login/', credentials),
  register: (userData) => api.post('/auth/register/', userData),
  refreshToken: (refreshToken) => api.post('/auth/token/refresh/', { refresh: refreshToken }),
  verifyEmail: (token) => api.post('/auth/verify-email/', { token }),
  resendVerification: () => api.post('/auth/resend-verification/'),
  
  // OTP-based registration
  registerOTP: (userData) => api.post('/auth/register-otp/', userData),
  verifyOTP: (email, otp) => api.post('/auth/verify-otp/', { email, otp }),
  resendOTP: (email) => api.post('/auth/resend-otp/', { email }),
  
  // Password management
  forgotPassword: (email) => api.post('/auth/password-reset-request/', { email }),
  resetPassword: (token, newPassword, confirmPassword) => 
    api.post('/auth/password-reset-confirm/', { token, new_password: newPassword, confirm_password: confirmPassword }),
  changePassword: (passwordData) => api.post('/auth/change-password/', passwordData),
  
  // User profile
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (profileData) => api.patch('/auth/update-user-info/', profileData),
  
  // Security
  getLoginAttempts: () => api.get('/auth/login-attempts/'),
  
  // User statistics
  getUserStats: () => api.get('/auth/user-stats/'),
  getRecentActivity: () => api.get('/auth/recent-activity/'),
};

export const portfolioAPI = {
  // Portfolio data
  getPortfolio: () => api.get('/portfolio/'),
  getHoldings: () => api.get('/portfolio/holdings/'),
  getTransactions: (params) => api.get('/portfolio/transactions/', { params }),
  
  // Performance
  getPerformance: (timeframe) => api.get(`/portfolio/performance/${timeframe}/`),
  getPerformanceHistory: (params) => api.get('/portfolio/performance-history/', { params }),
  
  // Analytics
  getAnalytics: () => api.get('/portfolio/analytics/'),
  getSectorAllocation: () => api.get('/portfolio/sector-allocation/'),
};

export const tradingAPI = {
  // Orders
  createOrder: (orderData) => api.post('/trading/orders/', orderData),
  getOrders: (params) => api.get('/trading/orders/', { params }),
  getPendingOrders: () => api.get('/trading/orders/pending/'),
  getOrderHistory: () => api.get('/trading/orders/history/'),
  cancelOrder: (orderId) => api.post(`/trading/orders/${orderId}/cancel/`),

  // Trades
  getTrades: (params) => api.get('/trading/trades/', { params }),

  // Watchlists (persistent, multi-tab)
  // NOTE: Response may be paginated (object with results) or array
  getWatchlists: () => api.get('/trading/watchlists/'),
  createWatchlist: (data) => api.post('/trading/watchlists/', data),
  renameWatchlist: (id, name) => api.post(`/trading/watchlists/${id}/rename/`, { name }),
  deleteWatchlist: (id) => api.delete(`/trading/watchlists/${id}/`),
  addToWatchlist: (id, symbol) => api.post(`/trading/watchlists/${id}/add_stock/`, { symbol }),
  removeFromWatchlist: (id, symbol) => api.post(`/trading/watchlists/${id}/remove_stock/`, { symbol }),
  clearWatchlist: (id) => api.post(`/trading/watchlists/${id}/clear/`),

  // Market status (public)
  getPublicMarketStatus: () => api.get('/trading/market-status/'),
};

export const marketDataAPI = {
  // Stock data
  searchStocks: (query) => api.get('/market-data/stocks/search/', { params: { q: query } }),
  getStockData: (symbol) => api.get(`/market-data/stocks/${symbol}/`),
  getStockPrice: (symbol) => api.get(`/market-data/stocks/${symbol}/price/`),
  
  // Market data
  getMarketOverview: () => api.get('/market-data/overview/'),
  getTopGainers: () => api.get('/market-data/top-gainers/'),
  getTopLosers: () => api.get('/market-data/top-losers/'),
  getMostActive: () => api.get('/market-data/most-active/'),
  
  // Historical data
  getHistoricalData: (symbol, timeframe) => api.get(`/market-data/stocks/${symbol}/historical/${timeframe}/`),
  
  // News
  getNews: (params) => api.get('/market-data/news/', { params }),
  getStockNews: (symbol) => api.get(`/market-data/stocks/${symbol}/news/`),
};

export const analyticsAPI = {
  // Performance analytics
  getPerformanceMetrics: (timeframe) => api.get(`/analytics/performance/${timeframe}/`),
  getRiskMetrics: () => api.get('/analytics/risk/'),
  getTradingMetrics: (params) => api.get('/analytics/trading/', { params }),
  
  // Reports
  generateReport: (reportType, params) => api.post(`/analytics/reports/${reportType}/`, params),
  getReports: () => api.get('/analytics/reports/'),
  
  // Leaderboard
  getLeaderboardStatus: () => api.get('/analytics/leaderboard/status/'),
  enrollLeaderboard: (autoRenew = false) => api.post('/analytics/leaderboard/enroll/', { auto_renew: autoRenew }),
  confirmLeaderboard: () => api.post('/analytics/leaderboard/confirm/'),
  leaveLeaderboard: () => api.post('/analytics/leaderboard/leave/'),
  getCurrentLeaderboard: () => api.get('/analytics/leaderboard/current/'),
};

export const learningAPI = {
  // Course levels and courses
  getLevels: () => api.get('/learning/levels/'),
  getCourses: (level = null) => api.get('/learning/courses/', { params: level ? { level } : {} }),
  getCourse: (courseId) => api.get(`/learning/courses/${courseId}/`),
  getLesson: (lessonId) => api.get(`/learning/lessons/${lessonId}/`),
  
  // User progress
  getCourseProgress: () => api.get('/learning/progress/courses/'),
  getLessonProgress: (courseId = null) => api.get('/learning/progress/lessons/', { 
    params: courseId ? { course_id: courseId } : {} 
  }),
  trackCourseTime: (courseId, deltaMinutes = 1) =>
    api.post('/learning/progress/course/time/', { course_id: courseId, delta_minutes: deltaMinutes }),
  
  // Certificates
  getCertificates: () => api.get('/learning/certificates/'),
  getCertificate: (certificateId) => api.get(`/learning/certificates/${certificateId}/`),
  getCertificatePdf: (certificateId) => api.get(`/learning/certificates/${certificateId}/pdf/`),
  
  // Actions
  enrollInCourse: (courseId) => api.post('/learning/enroll/', { course_id: courseId }),
  updateLessonProgress: (data) => api.post('/learning/progress/lesson/', data),
  updateCourseProgress: (data) => api.post('/learning/progress/course/', data),
  
  // Statistics
  getUserStats: () => api.get('/learning/stats/'),
};

export const journalAPI = {
  // Journal entries
  getEntries: (params) => api.get('/journal/entries/', { params }),
  getEntry: (entryId) => api.get(`/journal/entries/${entryId}/`),
  createEntry: (data) => api.post('/journal/entries/', data),
  updateEntry: (entryId, data) => api.put(`/journal/entries/${entryId}/`, data),
  deleteEntry: (entryId) => api.delete(`/journal/entries/${entryId}/`),
  
  // Templates
  getTemplates: () => api.get('/journal/templates/'),
  
  // Summary and stats
  getSummary: () => api.get('/journal/summary/'),
  getStats: () => api.get('/journal/stats/'),
  
  // Additional features
  searchEntries: (params) => api.get('/journal/search/', { params }),
  exportEntries: () => api.get('/journal/export/'),
  createFromTemplate: (data) => api.post('/journal/create-from-template/', data),

  // Upload attachments (images/files)
  uploadAttachment: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/journal/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Error handling utility
export const handleAPIError = (error, customMessage = null) => {
  let message = customMessage;

  if (error.response) {
    // Server responded with error status
    const { data, status } = error.response;
    
    if (data?.error) {
      message = data.error;
    } else if (data?.detail) {
      message = data.detail;
    } else if (data?.message) {
      message = data.message;
    } else {
      switch (status) {
        case 400:
          message = 'Bad request. Please check your input.';
          break;
        case 401:
          message = 'Unauthorized. Please log in again.';
          break;
        case 403:
          message = 'Access forbidden. You don\'t have permission for this action.';
          break;
        case 404:
          message = 'Resource not found.';
          break;
        case 422:
          message = 'Validation error. Please check your input.';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        default:
          message = 'An error occurred. Please try again.';
      }
    }
  } else if (error.request) {
    // Network error
    message = 'Network error. Please check your connection.';
  } else {
    // Other error
    message = error.message || 'An unexpected error occurred.';
  }

  toast.error(message);
  return message;
};

// Success handling utility
export const handleAPISuccess = (message = 'Operation completed successfully') => {
  toast.success(message);
};

export default api; 